import { GapErrorShape } from './GapErrorTypes'
import { namingConfig } from '@gap/utils/config/naming-config'

export class GapError extends Error {
   static triedUpdatingStaleRecord = class triedUpdatingStaleRecord extends GapError {
      constructor(message: string) {
         super(message, 409)
      }
   }

   static support = class support extends GapError {
      constructor(message: string) {
         super(message)
      }
   }

   static locked = class locked extends GapError {
      constructor(message: string = 'Resource is locked') {
         super(message, 423)
      }
   }

   static conflict = class conflict extends GapError {
      constructor(message: string) {
         super(message, 409)
      }
   }

   static unprocessable = class unprocessable extends GapError {
      constructor(message: string) {
         super(message, 422)
      }
   }

   static forbidden = class forbidden extends GapError {
      constructor(message: string = 'Not authorised to perform this action') {
         super(message, 403)
      }
   }

   static userIsDeactivated = class userIsDeactivated extends GapError {
      constructor(message: string = 'User deactivated') {
         super(message, 403)
      }
   }

   static notFound = class notFound extends GapError {
      constructor(message: string = 'Not found') {
         super(message, 404)
      }
   }

   static unauthorised = class unauthorised extends GapError {
      constructor(message: string = 'Not authorised to perform this action') {
         super(message, 401)
      }
   }

   /**
    * @deprecated The method should not be used. Instead, throw an error with a specific code or create a new one if your error code is not here
    */
   static generic = class generic extends GapError {
      constructor(message: string) {
         super(message)
      }
   }

   static messages = {
      forbidden: {
         noDocumentAccessAdvisor: 'This document does not belong to this advisor organisation',
         noDocumentAccessClient: 'This document does not belong to this client organisation',
         noDocumentAccessUser: 'User does not have access to the given document',
         noDocumentRights: "You don't have enough rights to view this document",
      },
      token: {
         expired: 'This token has expired',
         accessDenied: 'You do not have permission to access this token',
         usageLimitReached: 'This token has reached its usage limit',
      },
      auth: {
         userDoesNotExist: 'User does not exist',
         loginExpired: 'Login expired'
      },
      access: {
         noPermission: 'You do not have permission to access this resource',
         differentRole: 'You can only modify users with the same role as you',
         noPermissionToAccessOrg: 'You do not have permission to access this organisation',
      },
      conflict: {
         draftAlreadyExists: () => `A draft already exists for this ${namingConfig.documentTemplate.singular}.`,
      }
   }

   httpStatus: number
   gapErrorData: GapErrorShape

   protected constructor(message: string, httpStatus: number = 500) {
      super(message)
      this.name = `GapError.${this.constructor.name}`
      this.httpStatus = httpStatus
      this.gapErrorData = { gapErrorType: this.constructor.name as GapErrorSubclassName }
   }

   static format(errorCause: GapError) {
      return {
         message: errorCause.message,
         name: errorCause.name,
         gapErrorData: errorCause.gapErrorData,
      }
   }
}

/**
 * This should always be in sync with GapErrorType from @link file://./GapErrorTypes.ts
 */
type GapErrorSubclassName = Exclude<
   {
      [K in keyof typeof GapError]: (typeof GapError)[K] extends new (...args: any[]) => GapError ? K : never
   }[keyof typeof GapError],
   undefined
>
